import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import './SearchableSelect.css';

export interface SearchableSelectOption {
  value: string;
  label: string;
}

interface Props {
  /** Currently selected option value ('' = nothing selected / placeholder). */
  value: string;
  onChange: (next: string) => void;
  options: SearchableSelectOption[];
  /** Label shown on the trigger when no option is selected, and as the
   *  first "clear" entry inside the dropdown. */
  placeholder?: string;
  disabled?: boolean;
  /** Forwarded to the trigger button so existing CSS (.field-select,
   *  .template-select, error state) keeps styling it like the old <select>. */
  className?: string;
  /** Forwarded to the trigger button — preserves existing test selectors. */
  'data-testid'?: string;
  /** Placeholder for the search input (defaults to common.search). */
  searchPlaceholder?: string;
  /** Forwarded to the trigger button so a sibling <label htmlFor> still
   *  associates with the control. */
  id?: string;
  /** Accessible name for the trigger when there is no visible label. */
  ariaLabel?: string;
  /** Whether to offer a "clear" entry (selecting '') at the top of the list.
   *  Defaults to true. Set false to mirror a native select without an empty
   *  <option> (includeEmpty=false callers). */
  allowClear?: boolean;
}

/**
 * Client-side searchable replacement for a native <select>. The full option
 * set is already loaded in memory (clients, hardware types, templates…), so
 * filtering happens locally — no network round-trips. The popover renders in
 * a fixed-position layer to escape modal `overflow` clipping.
 */
const SearchableSelect: React.FC<Props> = ({
  value,
  onChange,
  options,
  placeholder = '',
  disabled = false,
  className = '',
  'data-testid': dataTestId,
  searchPlaceholder,
  id,
  ariaLabel,
  allowClear = true,
}) => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [popoverPos, setPopoverPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedLabel = useMemo(
    () => options.find((o) => o.value === value)?.label ?? '',
    [options, value],
  );

  // Filtered list, with the placeholder offered as a "clear" entry at the top.
  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const matches = needle
      ? options.filter(
          (o) =>
            o.label.toLowerCase().includes(needle) ||
            String(o.value).toLowerCase().includes(needle),
        )
      : options;
    const head: SearchableSelectOption[] =
      allowClear && placeholder && (!needle || placeholder.toLowerCase().includes(needle))
        ? [{ value: '', label: placeholder }]
        : [];
    return [...head, ...matches];
  }, [options, search, placeholder, allowClear]);

  // Reset the highlighted row to the current selection (or top) on each open
  // and whenever the filtered set changes.
  useEffect(() => {
    if (!open) return;
    const idx = filtered.findIndex((o) => o.value === value);
    setActiveIndex(idx >= 0 ? idx : 0);
  }, [open, filtered, value]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideRoot = rootRef.current?.contains(target);
      const insidePopover = document
        .querySelector('.searchable-select-popover')
        ?.contains(target);
      if (!insideRoot && !insidePopover) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // Position the popover under the trigger (fixed, to escape modal overflow).
  useEffect(() => {
    if (!open) {
      setPopoverPos(null);
      return;
    }
    const update = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      setPopoverPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open]);

  // Autofocus search when opening.
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const pick = useCallback(
    (next: string) => {
      onChange(next);
      setOpen(false);
      setSearch('');
    },
    [onChange],
  );

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = filtered[activeIndex];
      if (opt) pick(opt.value);
    }
  };

  return (
    <div ref={rootRef} className="searchable-select">
      <button
        ref={triggerRef}
        type="button"
        id={id}
        aria-label={ariaLabel}
        data-testid={dataTestId}
        className={`searchable-select-trigger ${className}`.trim()}
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={`searchable-select-value ${selectedLabel ? '' : 'placeholder'}`.trim()}>
          {selectedLabel || placeholder}
        </span>
        <span className="searchable-select-caret" aria-hidden="true">▾</span>
      </button>
      {open && popoverPos && (
        <div
          className="searchable-select-popover"
          style={{
            position: 'fixed',
            top: popoverPos.top,
            left: popoverPos.left,
            width: popoverPos.width,
          }}
          role="listbox"
        >
          <input
            ref={inputRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={searchPlaceholder || t('common.search')}
            className="searchable-select-search"
          />
          {filtered.length === 0 && (
            <div className="searchable-select-empty">{t('common.noResults')}</div>
          )}
          {filtered.map((opt, idx) => (
            <div
              key={`${opt.value}-${idx}`}
              className={`searchable-select-option${idx === activeIndex ? ' active' : ''}${opt.value === value ? ' selected' : ''}`}
              role="option"
              data-value={opt.value}
              aria-selected={opt.value === value}
              onMouseEnter={() => setActiveIndex(idx)}
              onClick={() => pick(opt.value)}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
